import { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import { AdminInquiryListShimmer } from '../common/Shimmer.jsx';
import { buildApiUrl } from '../../utils/apiBase.js';
import { devError } from '../../utils/logger.js';
import { useAdminDialog } from '../../contexts/AdminDialogContext.jsx';
import { useDestructiveAction } from '../../contexts/DestructiveActionContext.jsx';
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js';
import AdminRepository from '../../services/AdminRepository.js';
import AdminClinicDetail from './AdminClinicDetail.jsx';
import { useScreenProbe } from '../../utils/screenTiming.js';
import './AdminClinicList.css';
import './AdminInquiryManagement.css';

function MoreVertIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="2.25" />
      <circle cx="12" cy="12" r="2.25" />
      <circle cx="12" cy="19" r="2.25" />
    </svg>
  );
}

const STATUS_OPTIONS = [
  { value: 'new', label: '新規', className: 'badge-warning' },
  { value: 'in_progress', label: '対応中', className: 'badge-info' },
  { value: 'resolved', label: '解決済み', className: 'badge-success' },
  { value: 'closed', label: 'クローズ', className: 'badge-secondary' },
];

const SOURCE_OPTIONS = [
  { value: 'public', label: '公開フォーム' },
  { value: 'hub', label: 'ポータル' },
  { value: 'email_inbound', label: '受信メール' },
];

const SOURCE_LABELS = Object.fromEntries(SOURCE_OPTIONS.map((s) => [s.value, s.label]));

const CATEGORY_LABELS = {
  general: '一般',
  product: '製品',
  pricing: '料金',
  technical: '技術',
  partnership: '提携',
  other: 'その他',
};

const STATUS_MAP = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s]));

const INBOUND_SYNC_INTERVAL_MS = 5 * 60 * 1000;
const INBOUND_MAX_MESSAGES_KEY = 'admin.inboundSync.maxMessages';
const INBOUND_MAX_MESSAGES_OPTIONS = [50, 100, 200, 500];

function readStoredInboundMaxMessages() {
  try {
    const raw = localStorage.getItem(INBOUND_MAX_MESSAGES_KEY);
    const n = Number(raw);
    if (INBOUND_MAX_MESSAGES_OPTIONS.includes(n)) return n;
  } catch {
    // ignore
  }
  return 50;
}

function adjustAutoFieldHeight(el, { minHeightPx = 0 } = {}) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.max(minHeightPx, el.scrollHeight)}px`;
}

const DIRECTION_LABELS = {
  initial: '初回お問い合わせ',
  inbound: 'お客様返信',
  outbound: '管理者返信',
};

function mapInquiryToThreadRow(inq) {
  return {
    id: inq.threadId || inq.id,
    supportNumber: inq.supportNumber || null,
    status: inq.status || 'new',
    source: inq.source || 'public',
    participantName: inq.name,
    participantEmail: inq.email,
    primaryInquiryId: inq.id,
    preview: (inq.message || '').slice(0, 120),
    latestMessageAt: inq.createdAt,
    lastActivityAt: inq.createdAt,
  };
}

function normalizeInquiryListResponse(data) {
  if (Array.isArray(data.threads) && data.threads.length > 0) {
    return data.threads;
  }
  if (Array.isArray(data.inquiries) && data.inquiries.length > 0) {
    return data.inquiries.map(mapInquiryToThreadRow);
  }
  return [];
}

function toggleInSet(set, value) {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

const AdminInquiryManagement = () => {
  const probe = useScreenProbe('inquiries');
  const { showAlert, showConfirm } = useAdminDialog();
  const { requestDestructiveAction } = useDestructiveAction();
  const adminRepo = AdminRepository.getInstance();

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 400);

  const [statusTab, setStatusTab] = useState('new');
  const [activeSources, setActiveSources] = useState(new Set(['public', 'hub', 'email_inbound']));

  const [hasMore, setHasMore] = useState(false);
  const [lastId, setLastId] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});
  const [sourceCounts, setSourceCounts] = useState({});
  const [aiAvailable, setAiAvailable] = useState(false);
  const [aiUnavailableReason, setAiUnavailableReason] = useState('');

  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [threadDetail, setThreadDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const [replyBody, setReplyBody] = useState('');
  const [replySubject, setReplySubject] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [issuingSupport, setIssuingSupport] = useState(false);
  const [syncingInbound, setSyncingInbound] = useState(false);
  const [autoSyncingInbound, setAutoSyncingInbound] = useState(false);
  const [inboundMaxMessages, setInboundMaxMessages] = useState(() => readStoredInboundMaxMessages());
  const [inboundProgress, setInboundProgress] = useState(null);
  const inboundMaxMessagesRef = useRef(inboundMaxMessages);
  inboundMaxMessagesRef.current = inboundMaxMessages;
  const [activeSupportNumbers, setActiveSupportNumbers] = useState([]);
  const [assignSupportNumber, setAssignSupportNumber] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [lastInboundSyncAt, setLastInboundSyncAt] = useState(null);
  const [clinicDetailClinic, setClinicDetailClinic] = useState(null);
  const [openingClinicDetail, setOpeningClinicDetail] = useState(false);

  /** 一覧の複数選択（スレッド ID） */
  const [selectedThreadIds, setSelectedThreadIds] = useState(() => new Set());
  /** 三点メニューを開いているスレッド ID */
  const [menuOpenId, setMenuOpenId] = useState(null);
  /** 退場アニメ中も DOM に残すためのマウント ID */
  const [menuMountedId, setMenuMountedId] = useState(null);
  /** @type {[null | { action: 'delete' | 'status', label: string, current: number, total: number, succeeded: number, failed: number }, Function]} */
  const [bulkProgress, setBulkProgress] = useState(null);
  const bulkBusy = bulkProgress != null;

  const inboundSyncRunningRef = useRef(false);
  const selectedThreadIdRef = useRef(selectedThreadId);
  const replySubjectRef = useRef(null);
  const replyBodyRef = useRef(null);
  const menuRootRef = useRef(null);
  const menuCloseTimerRef = useRef(null);
  selectedThreadIdRef.current = selectedThreadId;

  const clearMenuCloseTimer = useCallback(() => {
    if (menuCloseTimerRef.current != null) {
      clearTimeout(menuCloseTimerRef.current);
      menuCloseTimerRef.current = null;
    }
  }, []);

  const dismissInquiryMenu = useCallback((immediate = false) => {
    clearMenuCloseTimer();
    setMenuOpenId(null);
    if (immediate) {
      setMenuMountedId(null);
      return;
    }
    menuCloseTimerRef.current = setTimeout(() => {
      setMenuMountedId(null);
      menuCloseTimerRef.current = null;
    }, 160);
  }, [clearMenuCloseTimer]);

  const openInquiryMenu = useCallback((threadId) => {
    clearMenuCloseTimer();
    setMenuMountedId(threadId);
    // マウント後に is-open を付けて入場アニメを発火
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setMenuOpenId(threadId);
      });
    });
  }, [clearMenuCloseTimer]);

  const toggleInquiryMenu = useCallback((threadId) => {
    if (menuOpenId === threadId) {
      dismissInquiryMenu();
      return;
    }
    openInquiryMenu(threadId);
  }, [menuOpenId, dismissInquiryMenu, openInquiryMenu]);

  const [inboundSyncEnabled, setInboundSyncEnabled] = useState(false);
  const loadListRef = useRef(null);
  const loadListReqIdRef = useRef(0);
  const loadList = useCallback(async ({
    append = false,
    startAfter = null,
    silent = false,
    countsOnly = false,
  } = {}) => {
    // countsOnly は一覧を書き換えないので世代を進めない。
    // silent の一覧置換は世代を進め、進行中の append との競合を防ぐ。
    const reqId = countsOnly ? loadListReqIdRef.current : ++loadListReqIdRef.current;
    if (!silent) {
      probe.start('load');
      setLoading(true);
      setError('');
    }
    try {
      const sources = activeSources.size === 0
        ? ''
        : activeSources.size === SOURCE_OPTIONS.length
          ? null
          : [...activeSources];

      const data = await adminRepo.listInquiriesPaginated({
        search: debouncedSearch,
        statuses: [statusTab],
        sources: sources === null ? null : (sources === '' ? [] : sources),
        statusSortOrder: statusTab,
        limit: 50,
        startAfter,
        view: 'threads',
      });

      if (reqId !== loadListReqIdRef.current) return;

      setStatusCounts(data.statusCounts || {});
      setSourceCounts(data.sourceCounts || {});
      setAiAvailable(Boolean(data.aiAvailable));
      setAiUnavailableReason(data.aiUnavailableReason || '');

      // ステータス変更後など: 件数だけ更新（一覧の silent 上書き競合を避ける）
      if (countsOnly) {
        return;
      }

      const next = normalizeInquiryListResponse(data);
      setThreads((prev) => (append ? [...prev, ...next] : next));
      if (!append) {
        dismissInquiryMenu(true);
        setSelectedThreadIds((prev) => {
          if (prev.size === 0) return prev;
          const visible = new Set(next.map((t) => t.id));
          let changed = false;
          const pruned = new Set();
          for (const id of prev) {
            if (visible.has(id)) pruned.add(id);
            else changed = true;
          }
          return changed ? pruned : prev;
        });
      }
      setHasMore(Boolean(data.hasMore));
      setLastId(data.lastId || null);
      setTotalCount(Number.isFinite(Number(data.total)) ? Number(data.total) : next.length);
      if (!silent) {
        probe.end('load', 'ok');
        probe.unlock();
        setInboundSyncEnabled(true);
      }
    } catch (err) {
      if (reqId !== loadListReqIdRef.current) return;
      devError('お問い合わせ一覧取得エラー:', err);
      if (!silent) {
        probe.end('load', `error: ${err?.message || 'unknown'}`);
        setError(err.message);
      }
    } finally {
      if (!silent && reqId === loadListReqIdRef.current) setLoading(false);
    }
  }, [adminRepo, debouncedSearch, statusTab, activeSources, probe, dismissInquiryMenu]);

  loadListRef.current = loadList;

  const loadThreadDetailRef = useRef(null);
  useEffect(() => { loadList({ append: false }); }, [loadList]);

  const loadThreadDetail = useCallback(async (threadId, { inquiryId = null, silent = false } = {}) => {
    if (!silent) {
      setDetailLoading(true);
      setThreadDetail(null);
    }
    try {
      const [detail, supportData] = await Promise.all([
        adminRepo.fetchInquiryThread(threadId, { inquiryId }),
        adminRepo.fetchActiveSupportNumbers(),
      ]);
      if (!detail?.thread) {
        throw new Error('スレッド詳細を取得できませんでした。バックエンドを再起動してください。');
      }
      setThreadDetail(detail);
      setReplySubject(detail.suggestedReplySubject || '');
      setActiveSupportNumbers(supportData.items || []);
      setAiAvailable(Boolean(detail.aiAvailable));
      setAiUnavailableReason(detail.aiUnavailableReason || '');
    } catch (err) {
      devError('スレッド詳細取得エラー:', err);
      setThreadDetail(null);
      if (!silent) await showAlert({ message: err.message, variant: 'error', title: 'エラー' });
    } finally {
      if (!silent) setDetailLoading(false);
    }
  }, [adminRepo, showAlert]);

  loadThreadDetailRef.current = loadThreadDetail;

  useLayoutEffect(() => {
    adjustAutoFieldHeight(replySubjectRef.current, { minHeightPx: 40 });
  }, [replySubject]);

  useLayoutEffect(() => {
    adjustAutoFieldHeight(replyBodyRef.current, { minHeightPx: 128 });
  }, [replyBody, threadDetail]);

  const runInboundSync = useCallback(async ({ manual = false } = {}) => {
    // 自動取込中は手動ボタンを UI で無効化する（競合時はスキップ）
    if (inboundSyncRunningRef.current) return null;

    inboundSyncRunningRef.current = true;
    const maxMessages = inboundMaxMessagesRef.current || 50;
    if (manual) {
      setSyncingInbound(true);
      setInboundProgress({
        phase: 'connecting',
        label: 'メールボックスに接続中',
        current: 0,
        total: 0,
        linked: 0,
        skipped: 0,
      });
    } else {
      setAutoSyncingInbound(true);
    }
    try {
      const result = await adminRepo.syncInboundInquiryEmails({
        maxMessages,
        stream: manual,
        onProgress: manual
          ? (evt) => {
            setInboundProgress({
              phase: evt.phase || 'scanning',
              label: evt.label || 'メールをスキャン中',
              current: Number(evt.current) || 0,
              total: Number(evt.total) || 0,
              linked: Number(evt.linked) || 0,
              skipped: Number(evt.skipped) || 0,
            });
          }
          : undefined,
      });
      setLastInboundSyncAt(new Date());
      if ((result.linked || 0) > 0 || (result.created || 0) > 0) {
        await loadListRef.current?.({ append: false, silent: !manual });
        const threadId = selectedThreadIdRef.current;
        if (threadId) {
          await loadThreadDetailRef.current?.(threadId, { silent: !manual });
        }
      }
      if (manual) {
        const errText = result.errors?.length ? `\n\n${result.errors.join('\n')}` : '';
        await showAlert({
          message: `処理: ${result.processed}件 / 新規: ${result.created || 0}件 / 紐付け: ${result.linked}件 / スキップ: ${result.skipped}件（取得上限 ${result.maxMessages || maxMessages}件）${errText}`,
          variant: result.errors?.length ? 'warning' : 'success',
          title: '受信メール取込',
        });
      }
      return result;
    } catch (err) {
      const is404 = String(err.message || '').includes('404');
      if (manual) {
        await showAlert({
          message: is404
            ? '受信メール取込 API が見つかりません。バックエンド（npm run dev）を最新コードで再起動してください。'
            : err.message,
          variant: 'error',
          title: '取込エラー',
        });
      } else if (!is404) {
        devError('受信メール取込エラー:', err);
      }
      return null;
    } finally {
      inboundSyncRunningRef.current = false;
      if (manual) {
        setSyncingInbound(false);
        setInboundProgress(null);
      } else {
        setAutoSyncingInbound(false);
      }
    }
  }, [adminRepo, showAlert]);

  const handleInboundMaxMessagesChange = (value) => {
    const n = Number(value);
    if (!INBOUND_MAX_MESSAGES_OPTIONS.includes(n)) return;
    setInboundMaxMessages(n);
    inboundMaxMessagesRef.current = n;
    try {
      localStorage.setItem(INBOUND_MAX_MESSAGES_KEY, String(n));
    } catch {
      // ignore
    }
  };

  const runInboundSyncRef = useRef(runInboundSync);
  runInboundSyncRef.current = runInboundSync;

  useEffect(() => {
    if (!inboundSyncEnabled) return undefined;
    const startId = setTimeout(() => {
      runInboundSyncRef.current();
    }, 1500);
    const intervalId = setInterval(() => {
      runInboundSyncRef.current();
    }, INBOUND_SYNC_INTERVAL_MS);
    return () => {
      clearTimeout(startId);
      clearInterval(intervalId);
    };
  }, [inboundSyncEnabled]);

  const openThread = (row) => {
    const threadId = typeof row === 'string' ? row : row.id;
    const inquiryId = typeof row === 'object' ? row.primaryInquiryId : null;
    setSelectedThreadId(threadId);
    setReplyBody('');
    setReplySubject('');
    setAssignSupportNumber('');
    loadThreadDetail(threadId, { inquiryId });
  };

  const closeDetail = () => {
    setSelectedThreadId(null);
    setThreadDetail(null);
    setReplyBody('');
    setReplySubject('');
    setClinicDetailClinic(null);
  };

  const handleOpenClinicDetail = async (clinicId) => {
    if (!clinicId) return;
    setOpeningClinicDetail(true);
    try {
      const data = await adminRepo.getClinicDetail(clinicId);
      if (!data?.clinic) {
        throw new Error('クリニック情報を取得できませんでした');
      }
      setClinicDetailClinic(data.clinic);
    } catch (err) {
      await showAlert({ message: err.message, variant: 'error', title: 'エラー' });
    } finally {
      setOpeningClinicDetail(false);
    }
  };

  const applyStatusChange = async (inquiryId, newStatus, threadId, { notify = true } = {}) => {
    setUpdatingStatus(inquiryId);
    try {
      const res = await fetch(buildApiUrl(`/api/contact/inquiries/${inquiryId}/status`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'ステータスの更新に失敗しました');
      }
      const leftCurrentTab = newStatus !== statusTab;
      setThreads((prev) => {
        if (leftCurrentTab) return prev.filter((t) => t.id !== threadId);
        return prev.map((t) => (t.id === threadId ? { ...t, status: newStatus } : t));
      });
      if (threadDetail?.thread?.id === threadId) {
        setThreadDetail((prev) => ({
          ...prev,
          thread: { ...prev.thread, status: newStatus },
        }));
      }
      loadListRef.current?.({ append: false, silent: true, countsOnly: true });
      return true;
    } catch (err) {
      if (notify) {
        await showAlert({ message: err.message, variant: 'error', title: 'エラー' });
      }
      return false;
    } finally {
      setUpdatingStatus(null);
    }
  };

  const handleStatusChange = (inquiryId, newStatus, threadId) => (
    applyStatusChange(inquiryId, newStatus, threadId, { notify: true })
  );

  const resolveActionTargets = useCallback((threadId) => {
    if (selectedThreadIds.has(threadId) && selectedThreadIds.size > 1) {
      return threads.filter((t) => selectedThreadIds.has(t.id));
    }
    const one = threads.find((t) => t.id === threadId);
    return one ? [one] : [];
  }, [selectedThreadIds, threads]);

  const toggleThreadSelection = (threadId, checked) => {
    setSelectedThreadIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(threadId);
      else next.delete(threadId);
      return next;
    });
  };

  const toggleSelectAllVisible = (checked) => {
    if (!checked) {
      setSelectedThreadIds(new Set());
      return;
    }
    setSelectedThreadIds(new Set(threads.map((t) => t.id)));
  };

  const deleteThreadById = async (threadId, reason) => {
    const res = await fetch(buildApiUrl(`/api/contact/inquiries/${encodeURIComponent(threadId)}`), {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || '削除に失敗しました');
    }
  };

  const handleMenuStatusChange = async (threadId, newStatus) => {
    dismissInquiryMenu();
    const targets = resolveActionTargets(threadId).filter((t) => t.status !== newStatus);
    if (targets.length === 0) {
      await showAlert({
        message: '対象のお問い合わせはすでにそのステータスです。',
        variant: 'info',
        title: 'ステータス変更',
      });
      return;
    }
    const label = STATUS_MAP[newStatus]?.label || newStatus;
    const ok = await showConfirm({
      title: 'ステータス変更',
      message: `${targets.length}件のお問い合わせを「${label}」に変更します。よろしいですか？`,
      confirmLabel: '変更する',
    });
    if (!ok) return;

    setBulkProgress({
      action: 'status',
      label: `「${label}」へ変更中`,
      current: 0,
      total: targets.length,
      succeeded: 0,
      failed: 0,
    });
    const failed = [];
    let succeeded = 0;
    for (let i = 0; i < targets.length; i += 1) {
      const t = targets[i];
      if (!t.primaryInquiryId) {
        failed.push(t.id);
        setBulkProgress((prev) => (prev ? {
          ...prev,
          current: i + 1,
          failed: failed.length,
        } : prev));
        continue;
      }
      const okUpdate = await applyStatusChange(t.primaryInquiryId, newStatus, t.id, {
        notify: targets.length === 1,
      });
      if (okUpdate) succeeded += 1;
      else failed.push(t.id);
      setBulkProgress((prev) => (prev ? {
        ...prev,
        current: i + 1,
        succeeded,
        failed: failed.length,
      } : prev));
    }
    setSelectedThreadIds((prev) => {
      const next = new Set(prev);
      for (const t of targets) {
        if (!failed.includes(t.id)) next.delete(t.id);
      }
      return next;
    });
    setBulkProgress(null);
    if (failed.length > 0) {
      await showAlert({
        message: `${succeeded}件を更新しましたが、${failed.length}件は失敗しました。`,
        variant: 'error',
        title: '一部失敗',
      });
    }
  };

  const handleMenuDelete = async (threadId) => {
    dismissInquiryMenu();
    const targets = resolveActionTargets(threadId);
    if (targets.length === 0) return;

    const result = await requestDestructiveAction({
      title: 'お問い合わせ削除',
      message: `${targets.length}件のお問い合わせスレッドを完全に削除します。関連メッセージも含めて取り消せません。`,
      confirmLabel: targets.length > 1 ? `${targets.length}件を削除する` : '削除する',
    });
    if (!result?.reason) return;

    setBulkProgress({
      action: 'delete',
      label: '削除中',
      current: 0,
      total: targets.length,
      succeeded: 0,
      failed: 0,
    });
    const deletedIds = [];
    const failed = [];
    for (let i = 0; i < targets.length; i += 1) {
      const t = targets[i];
      try {
        await deleteThreadById(t.id, result.reason);
        deletedIds.push(t.id);
        setThreads((prev) => prev.filter((row) => row.id !== t.id));
        setSelectedThreadIds((prev) => {
          const next = new Set(prev);
          next.delete(t.id);
          return next;
        });
        if (selectedThreadId === t.id) closeDetail();
      } catch (err) {
        failed.push({ id: t.id, message: err.message });
      }
      setBulkProgress((prev) => (prev ? {
        ...prev,
        current: i + 1,
        succeeded: deletedIds.length,
        failed: failed.length,
      } : prev));
    }
    loadListRef.current?.({ append: false, silent: true, countsOnly: true });
    setBulkProgress(null);

    if (failed.length > 0) {
      await showAlert({
        message: `${deletedIds.length}件を削除しましたが、${failed.length}件は失敗しました。`,
        variant: 'error',
        title: '一部失敗',
      });
    }
  };

  const handleRegenerateSupportNumber = async () => {
    if (!selectedThreadId) return;
    const current = threadDetail?.thread?.supportNumber;
    const ok = await showConfirm({
      title: 'サポート番号を再生成',
      message: current
        ? `現在の番号 ${current} を新しい番号に置き換えます。\n既存メールのスレッド紐付けに影響する場合があります。`
        : '新しいサポート番号を生成します。',
      confirmLabel: '再生成する',
      danger: true,
    });
    if (!ok) return;

    setIssuingSupport(true);
    try {
      const result = await adminRepo.issueSupportNumber(selectedThreadId, { regenerate: true });
      await showAlert({
        message: result.regenerated
          ? `サポート番号を再生成しました: ${result.supportNumber}`
          : `サポート番号: ${result.supportNumber}`,
        variant: 'success',
        title: 'サポート番号',
      });
      await loadThreadDetail(selectedThreadId);
      loadList({ append: false });
    } catch (err) {
      await showAlert({ message: err.message, variant: 'error', title: 'エラー' });
    } finally {
      setIssuingSupport(false);
    }
  };

  const handleGenerateReply = async () => {
    if (!selectedThreadId) return;
    setAiGenerating(true);
    try {
      const result = await adminRepo.generateInquiryReplyDraft(selectedThreadId);
      setReplyBody(result.draft || '');
      if (result.subject) setReplySubject(result.subject);
    } catch (err) {
      await showAlert({ message: err.message, variant: 'error', title: 'AI 生成エラー' });
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSendReply = async () => {
    if (!selectedThreadId || !replyBody.trim()) return;
    setReplySending(true);
    try {
      await adminRepo.sendInquiryReply(selectedThreadId, {
        body: replyBody,
        subject: replySubject || undefined,
      });
      await showAlert({ message: '返信メールを送信しました。', variant: 'success', title: '送信完了' });
      setReplyBody('');
      setReplySubject('');
      await loadThreadDetail(selectedThreadId);
      loadList({ append: false });
    } catch (err) {
      await showAlert({ message: err.message, variant: 'error', title: '送信エラー' });
    } finally {
      setReplySending(false);
    }
  };

  const handleAssignThread = async (inquiryId) => {
    if (!assignSupportNumber.trim()) {
      await showAlert({ message: 'サポート番号を選択または入力してください。', variant: 'warning', title: '入力不足' });
      return;
    }
    setAssigning(true);
    try {
      await adminRepo.assignInquiryToThread(inquiryId, { supportNumber: assignSupportNumber.trim() });
      await showAlert({ message: 'スレッドに紐付けました。', variant: 'success', title: '完了' });
      if (selectedThreadId) await loadThreadDetail(selectedThreadId);
      loadList({ append: false });
    } catch (err) {
      await showAlert({ message: err.message, variant: 'error', title: 'エラー' });
    } finally {
      setAssigning(false);
    }
  };

  const handleInboundSync = () => runInboundSync({ manual: true });

  const handleStatusTabChange = (nextStatus) => {
    if (nextStatus === statusTab) return;
    setSelectedThreadId(null);
    setThreadDetail(null);
    setSelectedThreadIds(new Set());
    dismissInquiryMenu(true);
    setStatusTab(nextStatus);
  };

  useEffect(() => {
    if (!menuOpenId) return undefined;
    const onPointerDown = (e) => {
      if (menuRootRef.current && !menuRootRef.current.contains(e.target)) {
        dismissInquiryMenu();
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') dismissInquiryMenu();
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpenId, dismissInquiryMenu]);

  useEffect(() => () => clearMenuCloseTimer(), [clearMenuCloseTimer]);

  const allVisibleSelected = useMemo(
    () => threads.length > 0 && threads.every((t) => selectedThreadIds.has(t.id)),
    [threads, selectedThreadIds],
  );
  const someVisibleSelected = useMemo(
    () => threads.some((t) => selectedThreadIds.has(t.id)),
    [threads, selectedThreadIds],
  );

  const formatDate = (isoStr) => {
    if (!isoStr) return '—';
    try {
      return new Date(isoStr).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
    } catch {
      return isoStr;
    }
  };

  const primaryInquiry = threadDetail?.inquiries?.find(
    (i) => i.id === threadDetail?.thread?.primaryInquiryId
  ) || threadDetail?.inquiries?.[0];

  return (
    <div className="admin-list-container">
      <div className="list-header">
        <div className="section-header">
          <h1>お問い合わせ管理</h1>
          <p>
            {STATUS_MAP[statusTab]?.label || 'スレッド'} {totalCount} 件
            {lastInboundSyncAt && (
              <span style={{ marginLeft: '0.75rem', fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                受信確認: {lastInboundSyncAt.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}（5分おき自動）
              </span>
            )}
          </p>
        </div>
        <div className="inquiry-list-header-actions">
          <label className="inquiry-inbound-limit">
            <span>取得件数</span>
            <select
              value={String(inboundMaxMessages)}
              onChange={(e) => handleInboundMaxMessagesChange(e.target.value)}
              disabled={syncingInbound}
              aria-label="受信メールの取得件数"
            >
              {INBOUND_MAX_MESSAGES_OPTIONS.map((n) => (
                <option key={n} value={String(n)}>{n}件</option>
              ))}
            </select>
          </label>
          <div className="inquiry-inbound-sync-control">
            <button
              type="button"
              onClick={handleInboundSync}
              className="btn btn-secondary btn-sm"
              disabled={syncingInbound || autoSyncingInbound}
              title={autoSyncingInbound ? '自動取込の完了後に操作できます' : undefined}
            >
              {syncingInbound ? '取込中…' : '受信メール取込'}
            </button>
            {autoSyncingInbound && (
              <span className="inquiry-inbound-auto-hint" role="status">
                自動取込処理中…
              </span>
            )}
          </div>
          <button type="button" onClick={() => loadList({ append: false })} className="btn btn-secondary" disabled={loading || syncingInbound}>
            再読み込み
          </button>
        </div>
      </div>

      <div className="inquiry-stats-bar">
        <span className="stat-chip">公開 {sourceCounts.public ?? 0}</span>
        <span className="stat-chip">ポータル {sourceCounts.hub ?? 0}</span>
        <span className="stat-chip">受信メール {sourceCounts.email_inbound ?? 0}</span>
        {(sourceCounts.unknown ?? 0) > 0 && (
          <span className="stat-chip">未分類 {sourceCounts.unknown}</span>
        )}
      </div>

      {error && <div className="error-message" style={{ color: 'var(--color-danger)', padding: '1rem' }}>{error}</div>}

      <div className="inquiry-status-tabs" role="tablist" aria-label="ステータス">
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.value}
            type="button"
            role="tab"
            aria-selected={statusTab === s.value}
            className={`inquiry-status-tab${statusTab === s.value ? ' active' : ''}`}
            onClick={() => handleStatusTabChange(s.value)}
          >
            {s.label}
            <span className="inquiry-status-tab__count">{statusCounts[s.value] ?? 0}</span>
          </button>
        ))}
      </div>

      <div className="inquiry-filter-toggles">
        <div className="inquiry-filter-group">
          <label>お問い合わせ元</label>
          <div className="inquiry-toggle-row">
            {SOURCE_OPTIONS.map((src) => (
              <button
                key={src.value}
                type="button"
                className={`inquiry-toggle-btn ${activeSources.has(src.value) ? 'active' : ''}`}
                onClick={() => setActiveSources((prev) => toggleInSet(prev, src.value))}
              >
                {src.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <AdminInquiryListShimmer rows={6} withHeader={false} />
      ) : (
        <>
          <div className="clinic-toolbar">
            <div className="search-bar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 16, height: 16 }}>
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="名前・メール・サポート番号で検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {threads.length > 0 && (
              <label className="inquiry-select-all">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                  }}
                  onChange={(e) => toggleSelectAllVisible(e.target.checked)}
                  disabled={bulkBusy}
                />
                <span>
                  {selectedThreadIds.size > 0
                    ? `${selectedThreadIds.size}件選択中`
                    : 'すべて選択'}
                </span>
              </label>
            )}
          </div>

          <div className="list-content">
            {threads.length === 0 ? (
              <div className="empty-state">
                <p>お問い合わせはありません</p>
              </div>
            ) : (
              threads.map((thread) => {
                const st = STATUS_MAP[thread.status] || STATUS_MAP.new;
                const src = thread.source || 'public';
                const isChecked = selectedThreadIds.has(thread.id);
                const isMenuOpen = menuOpenId === thread.id;
                const isMenuMounted = menuMountedId === thread.id;
                const menuTargetCount = (isChecked && selectedThreadIds.size > 1)
                  ? selectedThreadIds.size
                  : 1;
                return (
                  <div
                    key={thread.id}
                    className={`list-item inquiry-list-item ${selectedThreadId === thread.id ? 'selected' : ''} ${isChecked ? 'inquiry-list-item--checked' : ''}`}
                    onClick={() => openThread(thread)}
                    style={{ cursor: 'pointer' }}
                  >
                    <label
                      className="inquiry-row-check"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={bulkBusy}
                        onChange={(e) => toggleThreadSelection(thread.id, e.target.checked)}
                        aria-label={`${thread.participantName || thread.participantEmail || 'お問い合わせ'}を選択`}
                      />
                    </label>
                    <div className="inquiry-row-body">
                      <div className="item-header-with-actions">
                        <div className="item-header">
                          <strong>{thread.participantName || '—'}</strong>
                          <span className={`badge ${st.className}`}>{st.label}</span>
                          <span className={`badge badge-secondary inquiry-source-badge ${src}`} style={{ fontSize: '0.7rem' }}>
                            {SOURCE_OPTIONS.find((s) => s.value === src)?.label || src}
                          </span>
                          {thread.supportNumber && (
                            <span className="inquiry-support-badge">{thread.supportNumber}</span>
                          )}
                        </div>
                        <div
                          className="item-actions"
                          onClick={(e) => e.stopPropagation()}
                          ref={isMenuMounted ? menuRootRef : undefined}
                        >
                          <div className={`inquiry-menu${isMenuOpen ? ' is-open' : ''}`}>
                            <button
                              type="button"
                              className="inquiry-menu-trigger"
                              aria-label="操作メニュー"
                              aria-haspopup="menu"
                              aria-expanded={isMenuOpen}
                              disabled={bulkBusy}
                              onClick={() => toggleInquiryMenu(thread.id)}
                            >
                              <MoreVertIcon />
                            </button>
                            {isMenuMounted && (
                              <div className="inquiry-menu-dropdown" role="menu" aria-hidden={!isMenuOpen}>
                                {menuTargetCount > 1 && (
                                  <div className="inquiry-menu-caption">
                                    選択中の{menuTargetCount}件に適用
                                  </div>
                                )}
                                {STATUS_OPTIONS.map((s) => (
                                  <button
                                    key={s.value}
                                    type="button"
                                    role="menuitem"
                                    className="inquiry-menu-item"
                                    tabIndex={isMenuOpen ? 0 : -1}
                                    disabled={bulkBusy || (menuTargetCount === 1 && thread.status === s.value)}
                                    onClick={() => handleMenuStatusChange(thread.id, s.value)}
                                  >
                                    {s.label}にする
                                  </button>
                                ))}
                                <div className="inquiry-menu-divider" role="separator" />
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="inquiry-menu-item inquiry-menu-item--danger"
                                  tabIndex={isMenuOpen ? 0 : -1}
                                  disabled={bulkBusy}
                                  onClick={() => handleMenuDelete(thread.id)}
                                >
                                  削除
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="item-meta">
                        <span>{thread.participantEmail}</span>
                        <span> | {formatDate(thread.latestMessageAt || thread.lastActivityAt)}</span>
                      </div>
                      <p className="inquiry-row-preview">
                        {thread.preview || '—'}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {hasMore && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => loadList({ append: true, startAfter: lastId })}
                disabled={loading}
              >
                さらに読み込む
              </button>
            </div>
          )}
        </>
      )}

      {selectedThreadId && (
        <div className="modal-overlay inquiry-thread-modal-overlay" onClick={closeDetail}>
          <div className="modal modal-wide inquiry-thread-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>お問い合わせスレッド</h2>
              <button type="button" className="btn btn-icon" onClick={closeDetail}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="modal-body">
              {detailLoading || !threadDetail?.thread ? (
                <p>{detailLoading ? '読み込み中…' : 'スレッド詳細を取得できませんでした。'}</p>
              ) : (
                <>
                  <div className="inquiry-thread-modal-summary">
                    <span className={`badge ${(STATUS_MAP[threadDetail.thread.status] || STATUS_MAP.new).className}`}>
                      {(STATUS_MAP[threadDetail.thread.status] || STATUS_MAP.new).label}
                    </span>
                    {threadDetail.thread.supportNumber ? (
                      <>
                        <span className="inquiry-support-badge">{threadDetail.thread.supportNumber}</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={handleRegenerateSupportNumber}
                          disabled={issuingSupport}
                        >
                          {issuingSupport ? '生成中…' : '再生成'}
                        </button>
                      </>
                    ) : (
                      <span className="inquiry-thread-summary-muted">サポート番号を生成中…</span>
                    )}
                  </div>

                  {threadDetail.participantContext && (
                    <div className="inquiry-participant-panel">
                      <div className="inquiry-participant-panel-header">
                        <h3>送信者情報</h3>
                        <span className={`inquiry-source-badge ${threadDetail.participantContext.source || 'public'}`}>
                          {SOURCE_LABELS[threadDetail.participantContext.source] || threadDetail.participantContext.source}
                        </span>
                      </div>
                      <dl className="inquiry-participant-dl">
                        <div>
                          <dt>お名前</dt>
                          <dd>{threadDetail.participantContext.name || '—'}</dd>
                        </div>
                        <div>
                          <dt>メール</dt>
                          <dd>{threadDetail.participantContext.email || '—'}</dd>
                        </div>
                        {threadDetail.participantContext.phone && (
                          <div>
                            <dt>電話</dt>
                            <dd>{threadDetail.participantContext.phone}</dd>
                          </div>
                        )}
                        <div>
                          <dt>所属</dt>
                          <dd>
                            {threadDetail.participantContext.affiliationLabel || '—'}
                            {threadDetail.participantContext.organization
                              && threadDetail.participantContext.clinicName
                              && threadDetail.participantContext.organization !== threadDetail.participantContext.clinicName && (
                                <span className="inquiry-participant-sub">
                                  （フォーム入力: {threadDetail.participantContext.organization}）
                                </span>
                            )}
                          </dd>
                        </div>
                        {threadDetail.participantContext.userDisplayName && (
                          <div>
                            <dt>ポータル表示名</dt>
                            <dd>{threadDetail.participantContext.userDisplayName}</dd>
                          </div>
                        )}
                        {threadDetail.participantContext.userId && (
                          <div>
                            <dt>ユーザー ID</dt>
                            <dd><code>{threadDetail.participantContext.userId}</code></dd>
                          </div>
                        )}
                        {threadDetail.participantContext.userRole && (
                          <div>
                            <dt>ロール</dt>
                            <dd>{threadDetail.participantContext.userRole}</dd>
                          </div>
                        )}
                        {threadDetail.participantContext.clinicId && (
                          <div>
                            <dt>クリニック ID</dt>
                            <dd><code>{threadDetail.participantContext.clinicId}</code></dd>
                          </div>
                        )}
                      </dl>
                      <div className="inquiry-participant-actions">
                        {threadDetail.participantContext.clinicFound ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleOpenClinicDetail(threadDetail.participantContext.clinicId)}
                            disabled={openingClinicDetail}
                          >
                            {openingClinicDetail ? '読み込み中…' : 'クリニック詳細を開く'}
                          </button>
                        ) : threadDetail.participantContext.clinicId ? (
                          <span className="inquiry-thread-summary-muted">
                            クリニック ID は記録されていますが、登録が見つかりませんでした
                          </span>
                        ) : null}
                        {threadDetail.participantContext.clinicIsDemo && (
                          <span className="badge badge-info">デモ</span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="inquiry-thread-messages">
                    {(threadDetail.messages || []).map((msg) => (
                      <div key={msg.id} className={`inquiry-msg ${msg.direction}`}>
                        <div className="inquiry-msg-meta">
                          {DIRECTION_LABELS[msg.direction] || msg.direction}
                          {' · '}
                          {formatDate(msg.createdAt)}
                          {msg.fromEmail && ` · ${msg.fromEmail}`}
                        </div>
                        {msg.subject && <div style={{ fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.8rem' }}>{msg.subject}</div>}
                        <div style={{ whiteSpace: 'pre-wrap' }}>{msg.body}</div>
                      </div>
                    ))}
                  </div>

                  {threadDetail.inquiries?.length > 0 && (
                    <details style={{ marginBottom: '1rem', fontSize: '0.85rem' }}>
                      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>関連お問い合わせ ({threadDetail.inquiries.length})</summary>
                      {threadDetail.inquiries.map((inq) => (
                        <div key={inq.id} style={{ marginTop: '0.5rem', padding: '0.5rem', background: 'var(--color-bg-secondary)', borderRadius: 6 }}>
                          <div>{inq.name} · {CATEGORY_LABELS[inq.category] || inq.category} · {formatDate(inq.createdAt)}</div>
                          <div style={{ color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>{inq.message?.slice(0, 200)}</div>
                        </div>
                      ))}
                    </details>
                  )}

                  {!threadDetail.thread.supportNumber && threadDetail.inquiries?.some((inq) => !inq.supportNumber) && (
                    <div className="inquiry-assign-box" style={{ padding: '0.75rem', border: '1px dashed var(--color-border)', borderRadius: 8 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>既存スレッドへ手動紐付け</div>
                      <div className="inquiry-action-row">
                        <select
                          value={assignSupportNumber}
                          onChange={(e) => setAssignSupportNumber(e.target.value)}
                          className="btn btn-sm"
                          style={{ minWidth: 200 }}
                        >
                          <option value="">サポート番号を選択…</option>
                          {activeSupportNumbers.map((item) => (
                            <option key={item.supportNumber} value={item.supportNumber}>
                              {item.supportNumber} — {item.participantName}
                            </option>
                          ))}
                        </select>
                        <input
                          type="text"
                          placeholder="または CS-YYYYMMDD-XXXX を入力"
                          value={assignSupportNumber}
                          onChange={(e) => setAssignSupportNumber(e.target.value)}
                          style={{ flex: 1, minWidth: 160, padding: '0.35rem 0.5rem', borderRadius: 6, border: '1px solid var(--color-border)' }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          disabled={assigning}
                          onClick={() => handleAssignThread(threadDetail.inquiries[0]?.id)}
                        >
                          紐付け
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="inquiry-reply-box">
                    <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.5rem' }}>返信</h3>
                    <textarea
                      ref={replySubjectRef}
                      rows={1}
                      className="inquiry-reply-subject"
                      placeholder="件名（自動設定・編集可）"
                      value={replySubject}
                      onChange={(e) => setReplySubject(e.target.value)}
                    />
                    <textarea
                      ref={replyBodyRef}
                      className="inquiry-reply-body"
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      placeholder="返信本文…"
                    />
                    <div className="inquiry-action-row">
                      <button
                        type="button"
                        className="btn btn-sm btn-secondary"
                        onClick={handleGenerateReply}
                        disabled={aiGenerating}
                        title={!aiAvailable ? (aiUnavailableReason || 'AI が未設定です') : 'スレッド内容から返信文の下書きを生成'}
                      >
                        {aiGenerating ? 'AI 生成中…' : 'AI で返信文を生成'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={handleSendReply}
                        disabled={replySending || !replyBody.trim() || !threadDetail.thread.supportNumber}
                        title={!threadDetail.thread.supportNumber ? '先にサポート番号を発行してください' : ''}
                      >
                        {replySending ? '送信中…' : '返信を送信'}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer inquiry-thread-modal-footer">
              <div className="inquiry-thread-footer-status">
                {primaryInquiry && (
                  <>
                    <span className="inquiry-thread-footer-label">ステータス</span>
                    <div className="inquiry-thread-footer-status-btns">
                      {STATUS_OPTIONS.map((s) => (
                        <button
                          key={s.value}
                          type="button"
                          className={`btn btn-sm ${threadDetail?.thread?.status === s.value ? 'btn-primary' : 'btn-secondary'}`}
                          onClick={() => handleStatusChange(primaryInquiry.id, s.value, selectedThreadId)}
                          disabled={updatingStatus === primaryInquiry.id || threadDetail?.thread?.status === s.value}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              <button type="button" className="btn btn-secondary" onClick={closeDetail}>閉じる</button>
            </div>
            {clinicDetailClinic && (
              <div className="inquiry-clinic-detail-overlay" onClick={() => setClinicDetailClinic(null)}>
                <div className="inquiry-clinic-detail-panel" onClick={(e) => e.stopPropagation()}>
                  <AdminClinicDetail
                    clinic={clinicDetailClinic}
                    clinicStats={{}}
                    users={[]}
                    clinics={[]}
                    licenseLimit={30}
                    onBack={() => setClinicDetailClinic(null)}
                    onUpdate={() => {}}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {bulkProgress && (
        <div className="inquiry-bulk-progress-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="inquiry-bulk-progress-card">
            <div className="inquiry-bulk-progress-title">
              {bulkProgress.action === 'delete' ? 'お問い合わせを削除しています' : 'ステータスを変更しています'}
            </div>
            <p className="inquiry-bulk-progress-label">{bulkProgress.label}</p>
            <div
              className="inquiry-bulk-progress-bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={bulkProgress.total}
              aria-valuenow={bulkProgress.current}
              aria-label={`${bulkProgress.current} / ${bulkProgress.total}`}
            >
              <div
                className="inquiry-bulk-progress-bar__fill"
                style={{
                  width: `${bulkProgress.total > 0
                    ? Math.min(100, Math.round((bulkProgress.current / bulkProgress.total) * 100))
                    : 0}%`,
                }}
              />
            </div>
            <div className="inquiry-bulk-progress-meta">
              <span>
                {bulkProgress.current} / {bulkProgress.total} 件
                {bulkProgress.total > 0 && (
                  <>（{Math.min(100, Math.round((bulkProgress.current / bulkProgress.total) * 100))}%）</>
                )}
              </span>
              <span className="inquiry-bulk-progress-counts">
                成功 {bulkProgress.succeeded}
                {bulkProgress.failed > 0 && (
                  <span className="inquiry-bulk-progress-failed"> / 失敗 {bulkProgress.failed}</span>
                )}
              </span>
            </div>
          </div>
        </div>
      )}
      {inboundProgress && (
        <div className="inquiry-bulk-progress-overlay" role="status" aria-live="polite" aria-busy="true">
          <div className="inquiry-bulk-progress-card">
            <div className="inquiry-bulk-progress-title">受信メールを取り込んでいます</div>
            <p className="inquiry-bulk-progress-label">{inboundProgress.label}</p>
            <div
              className="inquiry-bulk-progress-bar"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={Math.max(inboundProgress.total, 1)}
              aria-valuenow={inboundProgress.current}
              aria-label={inboundProgress.total > 0
                ? `${inboundProgress.current} / ${inboundProgress.total}`
                : inboundProgress.label}
            >
              <div
                className={`inquiry-bulk-progress-bar__fill${inboundProgress.total <= 0 ? ' inquiry-bulk-progress-bar__fill--indeterminate' : ''}`}
                style={{
                  width: inboundProgress.total > 0
                    ? `${Math.min(100, Math.round((inboundProgress.current / inboundProgress.total) * 100))}%`
                    : undefined,
                }}
              />
            </div>
            <div className="inquiry-bulk-progress-meta">
              <span>
                {inboundProgress.total > 0
                  ? (
                    <>
                      {inboundProgress.current} / {inboundProgress.total} 件
                      （{Math.min(100, Math.round((inboundProgress.current / inboundProgress.total) * 100))}%）
                    </>
                  )
                  : `取得上限 ${inboundMaxMessages}件`}
              </span>
              <span className="inquiry-bulk-progress-counts">
                紐付け {inboundProgress.linked}
                {inboundProgress.skipped > 0 && <> / スキップ {inboundProgress.skipped}</>}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInquiryManagement;
